import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { EyeInvisibleOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useTypedTranslation } from 'translations/typedTranslations';
import { Colegios, Cursos, Instituciones } from 'generated/graphql';
import { useQuery } from 'react-apollo';
import SingleSelector from 'uiComps/selectors/SingleSelector';
import { getCursoName } from 'utils/getCursoName';
import {
	GET_CURSOS_BY_COLEGIO_AND_RUTAS_BY_YEAR,
	GET_CURSOS_BY_COLEGIO_AND_RUTAS_BY_YEAR_AND_YEAREND
} from 'components/cursos/cursos.graphql';
import { StyledInput } from 'utils/StyledInputForm';
import { sortCoursesMaxtoMin } from 'utils/sortCursos';
import { TipoUsuarioEnum } from 'enums/tipoUsuario';
import gql from 'graphql-tag';
import ForwardArrow from '../../../assets/f-arrow.svg';

const styles = {
	display: 'block',
	transform: 'translate(0, -50%)',
	top: '60%',
	right: '5%',
	width: '25px',
	fontStyle: 'normal',
	zIndex: 10
};

const IngresarStyledButton = styled.div`
	.ant-btn:hover,
	.ant-btn:focus,
	.ant-btn:active {
		background: #2d40f8;
	}
`;

const GET_ALL_COLEGIOS_AND_INSTITUCION_QUERY = gql`
	query getAllColegiosInstitucion($hasColegio: Boolean!) {
		colegios(where: { isDeleted: { _eq: false } }, order_by: { nombre: asc }) @include(if: $hasColegio) {
			key
			nombre
			pais
		}
		instituciones(order_by: { nombre: asc }) @skip(if: $hasColegio) {
			key
			nombre
			pais
		}
	}
`;

const SelectCurso: FC<{ colegioKey: string; handleRef: any; colegioPais: string }> = ({
	colegioKey,
	handleRef,
	colegioPais
}) => {
	const { t } = useTypedTranslation();
	const now = new Date();
	const currentYear = now.getFullYear();
	const currentMonth = now.getMonth();

	const yearBase = colegioPais === 'MX' ? (currentMonth < 6 ? currentYear - 1 : currentYear) : currentYear;

	const { data, loading, error } = useQuery<{
		cursos: Cursos[];
	}>(
		colegioPais === 'MX'
			? GET_CURSOS_BY_COLEGIO_AND_RUTAS_BY_YEAR_AND_YEAREND
			: GET_CURSOS_BY_COLEGIO_AND_RUTAS_BY_YEAR,
		{
			variables: {
				colegio_key: colegioKey,
				year: yearBase,
				...(colegioPais === 'MX' ? { yearEnd: yearBase + 1 } : {})
			}
		}
	);

	const defaultCursos = useMemo(
		() =>
			sortCoursesMaxtoMin(data?.cursos ?? []).map((curso) => ({
				value: curso.key,
				label: `${getCursoName(curso)}`
			})),
		[data?.cursos]
	);

	if (loading || error)
		return (
			<>
				<div className="relative justify-center my-2">{t('Cargando')}</div>
			</>
		);

	return (
		<>
			<div className="relative justify-center my-2">
				{t('Selecciona tu curso o curso')}
				<SingleSelector
					name="Colegio"
					options={defaultCursos}
					onChange={(e) => handleRef('cursoKey', e?.value)}
					isClearable
				/>
			</div>
		</>
	);
};

export const LoginContraseña: FC<{
	setPasswordValue: Dispatch<SetStateAction<string>>;
	hasError: any;
	noActive: any;
	onSubmitPassword: any;
	customLoader: any;
	setUserRef: Dispatch<
		SetStateAction<{
			cursoKey: string;
			nombre: string;
			apellido: string;
			contraseña: string;
			colegioKey: string;
			institucionKey: string;
		}>
	>;
	userRef: {
		cursoKey: string;
		nombre: string;
		apellido: string;
		contraseña: string;
		colegioKey: string;
		institucionKey: string;
	};
	currentUser: TipoUsuarioEnum;
}> = ({ setPasswordValue, hasError, noActive, onSubmitPassword, customLoader, setUserRef, userRef, currentUser }) => {
	const { t } = useTypedTranslation();
	const [passwordShown, setPasswordShown] = useState(false);

	const [colegio, setColegio] = useState('');
	const [pais, setPais] = useState('');
	const { cursoKey, contraseña } = userRef;
	const togglePasswordVisiblity = () => {
		setPasswordShown((shown) => !shown);
	};
	const hasColegio =
		currentUser === TipoUsuarioEnum.estudiante ||
		currentUser === TipoUsuarioEnum.profesor ||
		currentUser === TipoUsuarioEnum.coordinador;

	const { data, loading, error } = useQuery<{
		colegios: Colegios[];
		instituciones: Instituciones[];
	}>(GET_ALL_COLEGIOS_AND_INSTITUCION_QUERY, {
		variables: {
			hasColegio // True for students, teachers, coordinators; False for monitors
		}
	});

	const defaultColegios = (hasColegio ? data?.colegios : data?.instituciones)?.map((col) => ({
		value: col.key,
		label: col.nombre,
		pais: col.pais
	}));

	const handleSubmit = useCallback(
		(e?: React.FormEvent) => {
			if (e) e.preventDefault();
			setPasswordValue(contraseña);
			onSubmitPassword();
		},
		[contraseña, onSubmitPassword, setPasswordValue]
	);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Enter') {
				handleSubmit();
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleSubmit]);

	const handleRef = (e: string, data2: any) => {
		setUserRef((prev) => ({ ...prev, [e]: data2 }));
	};

	return (
		<>
			<form onSubmit={handleSubmit}>
				<div className="relative justify-center my-2">
					{t('Selecciona tu Institución')}
					{hasColegio ? (
						<SingleSelector
							name="Colegio"
							options={defaultColegios}
							onChange={(e) => {
								setColegio(e?.value);
								setPais(e?.pais);
								handleRef('colegioKey', e?.value);
							}}
							isClearable
							placeholder={t('Selecciona tu Institución')}
						/>
					) : (
						<SingleSelector
							name="Institucion"
							options={defaultColegios}
							onChange={(e) => {
								setColegio(e?.value);
								handleRef('institucionKey', e?.value);
							}}
							isClearable
							placeholder={t('Selecciona tu Institución')}
						/>
					)}
				</div>

				{colegio.length > 2 && currentUser === TipoUsuarioEnum.estudiante && (
					<SelectCurso colegioKey={colegio} handleRef={handleRef} colegioPais={pais} />
				)}

				{(cursoKey.length > 2 || (colegio.length > 2 && currentUser !== TipoUsuarioEnum.estudiante)) && (
					<div className="relative justify-center my-2">
						{t('Ingresa tu primer nombre')}
						<StyledInput
							name="nombre"
							onChange={(e) => handleRef('nombre', e.target.value)}
							isDirty={false}
							isSaved={false}
							isTouched={false}
							className="mb-2"
							autoComplete="name"
						/>
						{t('Ingresa tu primer apellido')}
						<StyledInput
							name="apellido"
							onChange={(e) => handleRef('apellido', e.target.value)}
							isDirty={false}
							isSaved={false}
							isTouched={false}
							className="mb-2"
							autoComplete="last-name"
						/>
					</div>
				)}
				{(cursoKey.length > 2 || (colegio.length > 2 && currentUser !== TipoUsuarioEnum.estudiante)) && (
					<div className="relative justify-center">
						{t('Ingresa tu contraseña Swarmob')}
						<StyledInput
							name="password"
							onChange={(e) => handleRef('contraseña', e.target.value)}
							isDirty={false}
							isSaved={false}
							isTouched={false}
							className="mb-2"
							type={passwordShown ? 'text' : 'password'}
							autoComplete="current-password"
						/>
						{passwordShown ? (
							<EyeOutlined onClick={togglePasswordVisiblity} className="absolute" style={styles} />
						) : (
							<EyeInvisibleOutlined
								onClick={togglePasswordVisiblity}
								className="absolute"
								style={styles}
							/>
						)}
					</div>
				)}

				{hasError && <div className="mb-5 text-swarm-red text-sm">{t('Usuario incorrecto')}</div>}
				{noActive && (
					<div className="mb-5 text-swarm-red text-sm">{t('Usuario no se encuentra habilitado.')}</div>
				)}

				<IngresarStyledButton className="flex mt-2 justify-between items-center w-full h-10 bg-clip-padding bg-swarm-orange text-white rounded-full cursor-pointer">
					<button
						type="submit"
						className="flex w-full justify-between items-center bg-transparent border-0 cursor-pointer"
					>
						<div className="ml-5" />
						<span className="font-semibold">{t('Ingresar')}</span>

						{customLoader ? (
							<div className="bg-swarm-purple text-white flex justify-center items-center mr-2 pb-2 w-8 h-8 rounded-full">
								<LoadingOutlined spin />
							</div>
						) : (
							<div className="bg-swarm-purple text-white flex justify-center items-center mr-2 w-8 h-8 rounded-full">
								<img src={ForwardArrow} alt="forward-arrow" />
							</div>
						)}
					</button>
				</IngresarStyledButton>
			</form>
		</>
	);
};
